import type { LabConfig, TestResult } from '../types';
import { loadWasmCpu } from '../wasm';
import { computeStats, now } from '../utils';

const GROUP = 'B_cpu';

function measureWasmWork(
  work: () => void,
  options: { runs: number; warmup: number }
): number[] {
  const { runs, warmup } = options;
  for (let i = 0; i < warmup; i++) work();
  const samples: number[] = [];
  for (let i = 0; i < runs; i++) {
    const t0 = now();
    work();
    samples.push(now() - t0);
  }
  return samples;
}

function makeResult(name: string, samples: number[], extra?: Record<string, unknown>): TestResult {
  return {
    name,
    group: GROUP,
    success: samples.length > 0,
    durationMs: samples.reduce((a, b) => a + b, 0),
    samples,
    stats: computeStats(samples),
    metadata: extra,
  };
}

export async function runCpuTests(config: LabConfig): Promise<TestResult[]> {
  const t0 = now();
  const tests: TestResult[] = [];

  const isProd = config.mode === 'prod';
  const runs = isProd ? 3 : 5;
  const warmup = 1;

  let wasm: Awaited<ReturnType<typeof loadWasmCpu>>;
  try {
    wasm = await loadWasmCpu('/cpu.wasm');
  } catch (err) {
    const error = err instanceof Error ? err.message : String(err);
    for (const name of ['integer_alu', 'float_alu', 'simd_like_integer', 'simd_like_float', 'branch_predictor', 'div_mod', 'bigint_ops']) {
      tests.push({ name, group: GROUP, success: false, durationMs: now() - t0, error: `WASM load failed: ${error}` });
    }
    return tests;
  }

  // Iterations tuned so each run is timer-resolvable but short.
  const w = {
    integer: isProd ? 500_000 : 5_000_000,
    float: isProd ? 250_000 : 1_000_000,
    simdInt: isProd ? 250_000 : 1_000_000,
    simdFloat: isProd ? 125_000 : 500_000,
    branch: isProd ? 1_000_000 : 10_000_000,
    divMod: isProd ? 250_000 : 2_500_000,
    bigint: isProd ? 50_000 : 250_000,
  };

  try {
    tests.push(makeResult('integer_alu', measureWasmWork(() => wasm.runIntegerAlu(w.integer), { runs, warmup })));
  } catch (err) {
    tests.push({ name: 'integer_alu', group: GROUP, success: false, durationMs: now() - t0, error: err instanceof Error ? err.message : String(err) });
  }

  if (!isProd) {
    try { tests.push(makeResult('float_alu', measureWasmWork(() => wasm.runFloatAlu(w.float), { runs, warmup }))); }
    catch (err) { tests.push({ name: 'float_alu', group: GROUP, success: false, durationMs: now() - t0, error: err instanceof Error ? err.message : String(err) }); }
    try { tests.push(makeResult('simd_like_integer', measureWasmWork(() => wasm.runSimdInt(w.simdInt), { runs, warmup }))); }
    catch (err) { tests.push({ name: 'simd_like_integer', group: GROUP, success: false, durationMs: now() - t0, error: err instanceof Error ? err.message : String(err) }); }
    try { tests.push(makeResult('simd_like_float', measureWasmWork(() => wasm.runSimdFloat(w.simdFloat), { runs, warmup }))); }
    catch (err) { tests.push({ name: 'simd_like_float', group: GROUP, success: false, durationMs: now() - t0, error: err instanceof Error ? err.message : String(err) }); }
  }

  try {
    const pred = measureWasmWork(() => wasm.runBranchPredict(w.branch, 1), { runs, warmup });
    const unpred = measureWasmWork(() => wasm.runBranchPredict(w.branch, 0), { runs, warmup });
    const predStats = computeStats(pred);
    const unpredStats = computeStats(unpred);
    tests.push({
      name: 'branch_predictor',
      group: GROUP,
      success: true,
      durationMs: predStats.median + unpredStats.median,
      samples: pred.concat(unpred),
      stats: computeStats([...pred, ...unpred]),
      ratios: { unpredictable_to_predictable: unpredStats.median / (predStats.median || 1) },
      metadata: {
        predictableMedian: predStats.median,
        unpredictableMedian: unpredStats.median,
        predictableCv: predStats.cv,
        unpredictableCv: unpredStats.cv,
      },
    });
  } catch (err) {
    tests.push({ name: 'branch_predictor', group: GROUP, success: false, durationMs: now() - t0, error: err instanceof Error ? err.message : String(err) });
  }

  try {
    tests.push(makeResult('div_mod', measureWasmWork(() => wasm.runDivMod(w.divMod), { runs, warmup })));
  } catch (err) {
    tests.push({ name: 'div_mod', group: GROUP, success: false, durationMs: now() - t0, error: err instanceof Error ? err.message : String(err) });
  }

  if (!isProd) {
    try {
      tests.push(makeResult('bigint_ops', measureWasmWork(() => wasm.runBigIntLike(w.bigint), { runs, warmup })));
    } catch (err) {
      tests.push({ name: 'bigint_ops', group: GROUP, success: false, durationMs: now() - t0, error: err instanceof Error ? err.message : String(err) });
    }
  }

  return tests;
}
